// Modal for answering (or reassigning) the open vendor Performance Scorecard review — the
// question-and-rating portion of the scorecard, surfaced next to What-If Explorer so business
// owners can act on it without opening the full Performance tab.
import React from "react";
import { X } from "lucide-react";

const STATUS_CFG = {
    Pending:   "bg-yellow-100 text-yellow-800",
    Overdue:   "bg-red-100 text-red-800",
    Completed: "bg-green-100 text-green-800",
};

const fmtDate = (d) => d ? new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "—";

const VendorPerformanceReviewModal = ({ isOpen, onClose, sc, canManage }) => {
    if (!isOpen) return null;

    const { catalog, users, actionError, setActionError, answers, setAnswers, comments, setComments,
            saving, open, canFill, themes, scale, liveThemeAvg, answeredCount, allAnswered,
            reassign, submit } = sc;

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl w-[680px] max-h-[90vh] overflow-y-auto">
                <div className="flex items-center justify-between p-4 border-b border-gray-200">
                    <h2 className="text-lg font-semibold">Vendor Performance Review</h2>
                    <button onClick={onClose} className="p-1 rounded hover:bg-gray-100">
                        <X className="h-5 w-5 text-gray-500" />
                    </button>
                </div>

                <div className="p-5">
                    {actionError && (
                        <div className="flex items-center justify-between gap-3 px-4 py-2.5 mb-4 bg-red-50 border border-red-200 rounded-lg">
                            <span className="text-xs text-red-700">{actionError}</span>
                            <button onClick={() => setActionError(null)} className="text-xs font-semibold text-red-400 hover:text-red-700">✕</button>
                        </div>
                    )}

                    {!open || !catalog ? (
                        <div className="py-10 text-sm text-gray-400 text-center">No open performance review to score.</div>
                    ) : (
                        <>
                            <div className="mb-5 pb-4 border-b border-gray-100">
                                <div className="flex items-center gap-2 mb-2">
                                    <span className="text-sm font-semibold text-gray-800">Performance Review — {open.reviewPeriod}</span>
                                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_CFG[open.status]}`}>{open.status}</span>
                                </div>
                                <div className="flex items-center flex-wrap gap-x-5 gap-y-1.5 text-xs text-gray-500">
                                    <span>Due <strong className="text-gray-700">{fmtDate(open.dueDate)}</strong></span>
                                    <span className="flex items-center gap-1.5">
                                        Reviewer
                                        {canManage ? (
                                            <select value={open.reviewerUserId || ""} onChange={e => reassign(e.target.value)}
                                                className="border border-gray-200 rounded-md px-1.5 py-0.5 text-xs bg-white text-gray-700">
                                                <option value="">Unassigned</option>
                                                {users.map(u => <option key={u.id} value={u.id}>{u.fullName}</option>)}
                                            </select>
                                        ) : <strong className="text-gray-700">{open.reviewerName || "Unassigned"}</strong>}
                                    </span>
                                </div>
                            </div>

                            {canFill ? (
                                <>
                                    <div className="flex items-center justify-between mb-1">
                                        <p className="text-xs text-gray-500">Rate the vendor's performance for this period</p>
                                        <p className="text-xs font-medium text-gray-600">{answeredCount}/{catalog.questions.length} answered</p>
                                    </div>
                                    <div className="w-full bg-gray-100 rounded-full h-1.5 mb-5">
                                        <div className="bg-indigo-600 h-1.5 rounded-full transition-all" style={{ width: `${(answeredCount / catalog.questions.length) * 100}%` }} />
                                    </div>
                                    {themes.map(({ theme, questions }, idx) => (
                                        <div key={theme} className={`mb-5 ${idx > 0 ? "pt-5 border-t border-gray-100" : ""}`}>
                                            <div className="flex items-center justify-between mb-2.5">
                                                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{theme}</span>
                                                <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-500">
                                                    Section avg <strong className="text-gray-700">{liveThemeAvg(questions)}</strong>
                                                </span>
                                            </div>
                                            <div className="space-y-2">
                                                {questions.map(q => (
                                                    <div key={q.questionId} className="border border-gray-100 rounded-lg p-3">
                                                        <div className="text-sm text-gray-700 mb-2.5">
                                                            <span className="text-gray-400 font-medium mr-1">{q.seq}.</span>{q.text}
                                                        </div>
                                                        <div className="flex flex-wrap gap-1.5">
                                                            {scale.map(opt => (
                                                                <button key={opt.value} onClick={() => setAnswers(p => ({ ...p, [q.questionId]: opt.value }))}
                                                                    className={`text-xs px-2.5 py-1.5 rounded-lg border font-medium transition-all
                                                                        ${answers[q.questionId] === opt.value
                                                                            ? "bg-indigo-600 text-white border-indigo-600"
                                                                            : "bg-white text-gray-600 border-gray-200 hover:border-indigo-300"}`}>
                                                                    {opt.value} · {opt.label}
                                                                </button>
                                                            ))}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    ))}
                                    <textarea value={comments} onChange={e => setComments(e.target.value)} rows={2}
                                        placeholder="Overall comments (optional)"
                                        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm mb-3" />
                                    <button onClick={async () => { if (await submit()) onClose(); }} disabled={!allAnswered || saving}
                                        className="px-5 py-2 bg-indigo-600 text-white rounded-lg text-sm font-semibold hover:bg-indigo-700 disabled:opacity-50">
                                        {saving ? "Submitting…" : allAnswered ? "Submit Review" : `Answer all ${catalog.questions.length} questions to submit`}
                                    </button>
                                </>
                            ) : (
                                <p className="text-xs text-gray-500 mt-2">
                                    Awaiting completion by <strong>{open.reviewerName || "the assigned reviewer"}</strong>.
                                </p>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default VendorPerformanceReviewModal;
